/* ───────────────────────────────────────────────────────────────────────────
   HeroApiSnippet — terminal-style request/response card pinned to the
   top-right of the hero. Reinforces the "infrastructure" word in the
   headline by showing the literal API surface: a curl call up top,
   the JSON response below. Aperture-Light: cream surface, mint hair-
   line, JetBrains Mono, low-key syntax highlighting in emerald + ink.
   Decorative — aria-hidden, hidden below xl.

   Server component (no client bundle cost): all motion is CSS, no
   state, no effects.
   ─────────────────────────────────────────────────────────────────────────── */

export default function HeroApiSnippet() {
  return (
    <div
      aria-hidden
      // Visible from xl (>=1280) so 13" laptops + larger get it.
      // Vertical anchor (top: 4rem) puts the card alongside the
      // upper part of the headline at every width — bumped up so it
      // sits closer to the trace eyebrow instead of drifting toward
      // the subhead.
      // Horizontal anchor uses max(2.5%, content-edge + 8px):
      //   - On narrow widths (<=1240+padding), 2.5% wins so the card
      //     sits in the screen's right margin.
      //   - On wider widths, the calc keeps the card aligned with the
      //     headline's right edge instead of drifting into the empty
      //     screen-margin and looking detached.
      className="absolute pointer-events-none hidden xl:block select-none"
      style={{
        top: "4rem",
        right: "max(2.5%, calc((100vw - 1240px) / 2 + 8px))",
        width: "min(340px, 22vw)",
        opacity: 0.92,
        fontFamily: "'JetBrains Mono', ui-monospace, monospace",
      }}
    >
      <style>{`
        @keyframes hero-api-caret {
          0%, 49%  { opacity: 1 }
          50%, 100%{ opacity: 0 }
        }
        /* Looping stream-in: the response card fades + slides up
           every ~6s, simulating "we just hit the endpoint". Adds
           ambient motion that signals "this is live infrastructure"
           without being noisy. Each cycle: 14% in, hold, 6% out. */
        @keyframes hero-api-stream-loop {
          0%        { opacity: 0;    transform: translateY(6px) }
          14%, 90%  { opacity: 0.95; transform: translateY(0)   }
          96%, 100% { opacity: 0.45; transform: translateY(0)   }
        }
        /* The trace dot at the top of the card pulses in sync so
           the eye reads the card as "running" instead of static. */
        @keyframes hero-api-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(15,81,50,0.45) }
          50%      { box-shadow: 0 0 0 6px rgba(15,81,50,0)   }
        }
      `}</style>

      {/* Anchor strip — same pattern we used before so the card feels
          tied to the page grid, not floating in the corner. */}
      <div className="flex items-center gap-2 mb-2 px-1">
        <span
          style={{
            width: 5, height: 5, borderRadius: 999,
            background: "#0F5132",
            boxShadow: "0 0 8px rgba(15,81,50,0.45)",
          }}
        />
        <span
          style={{
            fontSize: 9,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: "rgba(10,10,10,0.55)",
            fontWeight: 700,
          }}
        >
          POST · /api/v1/clean
        </span>
        <span
          aria-hidden
          className="flex-1"
          style={{
            height: 1,
            background:
              "linear-gradient(90deg, rgba(10,10,10,0.14), rgba(10,10,10,0))",
          }}
        />
      </div>

      {/* Request card */}
      <div
        className="relative rounded-xl overflow-hidden"
        style={{
          background: "rgba(255,255,255,0.72)",
          border: "1px solid rgba(10,10,10,0.10)",
          backdropFilter: "blur(6px)",
          boxShadow:
            "0 1px 0 rgba(255,255,255,0.7) inset, 0 18px 44px -28px rgba(10,10,10,0.18)",
        }}
      >
        <div
          aria-hidden
          className="absolute inset-x-3 top-0 h-px pointer-events-none"
          style={{
            background:
              "linear-gradient(90deg, transparent, rgba(15,81,50,0.45), transparent)",
          }}
        />

        {/* Tab strip */}
        <div
          className="flex items-center gap-3 px-3 py-2"
          style={{ borderBottom: "1px solid rgba(10,10,10,0.06)" }}
        >
          <span
            className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded"
            style={{
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "#0F5132",
              background: "rgba(15,81,50,0.08)",
              border: "1px solid rgba(15,81,50,0.18)",
            }}
          >
            POST
          </span>
          <span
            className="truncate"
            style={{
              fontSize: 11,
              color: "rgba(10,10,10,0.62)",
              letterSpacing: "-0.005em",
            }}
          >
            api.databridge.so/v1/clean
          </span>
          <span className="ml-auto" style={{ fontSize: 10, color: "rgba(10,10,10,0.42)" }}>200 OK</span>
        </div>

        {/* curl body */}
        <div className="px-3 py-3" style={{ fontSize: 11, lineHeight: 1.65 }}>
          <div style={{ color: "rgba(10,10,10,0.42)" }}>
            <span style={{ color: "#0F5132" }}>$</span> curl -X POST \
          </div>
          <div style={{ color: "rgba(10,10,10,0.62)", paddingLeft: 12 }}>
            api.databridge.so/v1/clean \
          </div>
          <div style={{ color: "rgba(10,10,10,0.62)", paddingLeft: 12 }}>
            -H <span style={{ color: "#0F5132" }}>"Authorization: Bearer ••••"</span> \
          </div>
          <div style={{ color: "rgba(10,10,10,0.62)", paddingLeft: 12 }}>
            -F <span style={{ color: "#0F5132" }}>"file=@list.csv"</span>
            <span
              className="inline-block ml-1 align-baseline"
              style={{
                width: 6, height: 11,
                background: "#0F5132",
                animation: "hero-api-caret 1.05s steps(1) infinite",
                verticalAlign: "-1px",
              }}
            />
          </div>
        </div>

        {/* Divider */}
        <div style={{ height: 1, background: "rgba(10,10,10,0.06)" }} />

        {/* Response — JSON */}
        <div
          className="px-3 py-3"
          style={{
            fontSize: 11,
            lineHeight: 1.65,
            background: "rgba(15,81,50,0.025)",
            animation: "hero-api-stream 1.6s ease-out 0.3s both",
          }}
        >
          <div style={{ color: "rgba(10,10,10,0.42)", marginBottom: 4 }}>
            ← <span style={{ color: "rgba(10,10,10,0.55)" }}>response.json</span>
          </div>
          <div>
            <span style={{ color: "rgba(10,10,10,0.42)" }}>{"{"}</span>
          </div>
          <div style={{ paddingLeft: 12 }}>
            <span style={{ color: "rgba(10,10,10,0.55)" }}>"processed"</span>
            <span style={{ color: "rgba(10,10,10,0.42)" }}>: </span>
            <span style={{ color: "#0A0A0A", fontWeight: 600 }}>12,408</span>
            <span style={{ color: "rgba(10,10,10,0.42)" }}>,</span>
          </div>
          <div style={{ paddingLeft: 12 }}>
            <span style={{ color: "rgba(10,10,10,0.55)" }}>"deliverable"</span>
            <span style={{ color: "rgba(10,10,10,0.42)" }}>: </span>
            <span style={{ color: "#0F5132", fontWeight: 600 }}>11,932</span>
            <span style={{ color: "rgba(10,10,10,0.42)" }}>,</span>
          </div>
          <div style={{ paddingLeft: 12 }}>
            <span style={{ color: "rgba(10,10,10,0.55)" }}>"removed"</span>
            <span style={{ color: "rgba(10,10,10,0.42)" }}>: </span>
            <span style={{ color: "#A33F3F", fontWeight: 600 }}>476</span>
            <span style={{ color: "rgba(10,10,10,0.42)" }}>,</span>
          </div>
          <div style={{ paddingLeft: 12 }}>
            <span style={{ color: "rgba(10,10,10,0.55)" }}>"audit_trail"</span>
            <span style={{ color: "rgba(10,10,10,0.42)" }}>: </span>
            <span style={{ color: "#0F5132" }}>"per_row"</span>
          </div>
          <div>
            <span style={{ color: "rgba(10,10,10,0.42)" }}>{"}"}</span>
          </div>
        </div>
      </div>

      {/* Footer meta */}
      <div className="flex items-center justify-between mt-2 px-1" style={{ fontSize: 9, color: "rgba(10,10,10,0.45)", letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 700 }}>
        <span>v1 · stable</span>
        <span>↳ docs at /docs/api</span>
      </div>
    </div>
  )
}
